import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useAICalendar } from "contexts/AICalendarContext";
import _request from "actions/_request";
import AppContext from "AppContext";
import BuyCreditsModal from "components/BuyCreditsModal";
import i18n from "i18";

/**
 * Step 2: Suggest Topics
 * Review and edit AI-suggested content topics
 * @returns {JSX.Element} Step 2 Topics editor
 */
export default function Step2Topics() {
  const { state, nextStep, prevStep, setTopics } = useAICalendar();
  const { metaData, refreshCredits } = useContext(AppContext);
  const isTrial = metaData?.ai_credits ? metaData.ai_credits.monthly_allowance === 0 : false;

  const [loading, setLoading] = useState(false);
  const [localTopics, setLocalTopics] = useState([]);
  const [customTopic, setCustomTopic] = useState("");
  const [localError, setLocalError] = useState(null);
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);

  // Load topics on mount if not already loaded
  useEffect(() => {
    if (state.topics.length > 0) {
      setLocalTopics(state.topics);
    } else {
      loadTopics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTopics = async () => {
    setLoading(true);
    setLocalError(null);

    try {
      const params = {
        strategy: state.strategy,
        // Pass the Step 1 business context so suggested topics stay specific to the
        // user's industry/audience instead of generic seasonal filler.
        business_context: state.businessContext,
        account_ids: state.selectedAccounts,
        language: i18n.language,
      };

      const response = await _request.aiCalendar.suggestTopics(params);

      if (response?.isInsufficientCredits) {
        setBuyCreditsOpen(true);
      } else if (response?.isOk && response?.data) {
        if (refreshCredits) refreshCredits();
        setLocalTopics(response.data.topics || []);
        setTopics(response.data.topics || []);
      } else {
        setLocalError(response?.error || i18n.t("aiCalendarWizard.errors.topicsFailed"));
      }
    } catch (error) {
      console.error("Topics loading failed:", error);
      setLocalError(error.message || i18n.t("aiCalendarWizard.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomTopic = () => {
    if (!customTopic.trim()) return;

    const newTopic = {
      id: `custom-${Date.now()}`,
      title: customTopic.trim(),
      description: "",
      category: "custom",
      priority: "medium",
      suggested_frequency: 1,
      suggested_platforms: [],
      keywords: [],
      content_ideas: [],
      rationale: i18n.t("aiCalendarWizard.step2.customTopic"),
    };

    const updated = [...localTopics, newTopic];
    setLocalTopics(updated);
    setCustomTopic("");
  };

  const handleRemoveTopic = (topicId) => {
    const updated = localTopics.filter((t) => t.id !== topicId);
    setLocalTopics(updated);
  };

  const handleNext = () => {
    if (localTopics.length === 0) {
      setLocalError(i18n.t("aiCalendarWizard.errors.noTopics"));
      return;
    }
    setTopics(localTopics);
    nextStep();
  };

  const getCategoryColor = (category) => {
    const colors = {
      promotional: "error",
      educational: "info",
      engagement: "success",
      announcement: "warning",
      storytelling: "primary",
      custom: "default",
    };
    return colors[category] || "default";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: "error",
      medium: "warning",
      low: "default",
    };
    return colors[priority] || "default";
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography variant="h6">
        {i18n.t("aiCalendarWizard.step2.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {i18n.t("aiCalendarWizard.step2.description")}
      </Typography>

      {loading && (
        <Box>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {i18n.t("aiCalendarWizard.analyzing")}
          </Typography>
        </Box>
      )}

      {localError && (
        <Alert severity="error" onClose={() => setLocalError(null)}>
          {localError}
        </Alert>
      )}

      {/* Add Custom Topic */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {i18n.t("aiCalendarWizard.step2.addCustom")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            placeholder={i18n.t("aiCalendarWizard.step2.customPlaceholder")}
            value={customTopic}
            onChange={(e) => setCustomTopic(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") handleAddCustomTopic();
            }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddCustomTopic}
          >
            {i18n.t("aiCalendarWizard.step2.add")}
          </Button>
        </Box>
      </Box>

      {/* Topics List */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {i18n.t("aiCalendarWizard.step2.suggestedTopics")} ({localTopics.length})
        </Typography>
        <Grid container spacing={2}>
          {localTopics.map((topic) => (
            <Grid item xs={12} md={6} key={topic.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {topic.title}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveTopic(topic.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {topic.description}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                    <Chip
                      label={topic.category}
                      size="small"
                      color={getCategoryColor(topic.category)}
                    />
                    <Chip
                      label={topic.priority}
                      size="small"
                      color={getPriorityColor(topic.priority)}
                    />
                    {topic.suggested_frequency > 0 && (
                      <Chip
                        label={`${topic.suggested_frequency}x`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {topic.keywords && topic.keywords.length > 0 && (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {topic.keywords.slice(0, 5).map((keyword, idx) => (
                        <Chip
                          key={idx}
                          label={keyword}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 2 }}>
        <Button variant="outlined" onClick={prevStep}>
          {i18n.t("aiCalendarWizard.back")}
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={localTopics.length === 0}
        >
          {i18n.t("aiCalendarWizard.next")}
        </Button>
      </Box>

      <BuyCreditsModal
        open={buyCreditsOpen}
        onClose={() => setBuyCreditsOpen(false)}
        isTrial={isTrial}
      />
    </Box>
  );
}
